import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { AlertTriangle, CheckCircle2, X } from "lucide-react";
import { Button } from "@onemileauto/ui";
import { useOutboxToasts, type OutboxToast } from "../../hooks/use-outbox-toasts";

const SENT_AUTO_DISMISS_MS = 3000;

/** Ticks a re-render every `intervalMs` while `active` is true — drives the live countdown without a per-row timer. */
function useTick(active: boolean, intervalMs: number): void {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = window.setInterval(() => setTick((n) => n + 1), intervalMs);
    return () => window.clearInterval(id);
  }, [active, intervalMs]);
}

interface ToastRowProps {
  toast: OutboxToast;
  mailboxId: string;
}

function ToastRow({ toast, mailboxId }: ToastRowProps) {
  const { dismiss, undo, retry } = useOutboxToasts();
  const secondsLeft = Math.max(0, Math.ceil((new Date(toast.scheduledSendAt).getTime() - Date.now()) / 1000));

  useEffect(() => {
    if (toast.status !== "sent") return;
    const timer = window.setTimeout(() => dismiss(toast.outboxId), SENT_AUTO_DISMISS_MS);
    return () => window.clearTimeout(timer);
  }, [toast.status, toast.outboxId, dismiss]);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 16, scale: 0.96 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.15 } }}
      transition={{ type: "spring", stiffness: 400, damping: 32 }}
      className="pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-lg border border-charcoal-soft bg-charcoal px-4 py-3 text-parchment shadow-lg"
    >
      {toast.status === "pending" && (
        <>
          <div
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-rust-soft text-xs font-semibold tabular-nums"
            aria-hidden
          >
            {secondsLeft > 0 ? secondsLeft : "…"}
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{secondsLeft > 0 ? "Sending…" : "Sending now…"}</p>
            {toast.subject && <p className="truncate text-xs text-parchment/70">{toast.subject}</p>}
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="shrink-0 bg-parchment text-charcoal hover:bg-parchment-dim"
            onClick={() => undo(mailboxId, toast.outboxId)}
          >
            Undo
          </Button>
        </>
      )}

      {toast.status === "sent" && (
        <>
          <CheckCircle2 size={20} className="shrink-0 text-success" aria-hidden />
          <p className="min-w-0 flex-1 truncate text-sm font-medium">Message sent</p>
          <button
            type="button"
            onClick={() => dismiss(toast.outboxId)}
            aria-label="Dismiss"
            className="shrink-0 rounded-full p-1.5 text-parchment/70 hover:bg-parchment/10"
          >
            <X size={16} />
          </button>
        </>
      )}

      {toast.status === "failed" && (
        <>
          <AlertTriangle size={20} className="shrink-0 text-warning" aria-hidden />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">Couldn&rsquo;t send</p>
            <p className="truncate text-xs text-parchment/70">Check your connection and try again.</p>
          </div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            className="shrink-0 bg-parchment text-charcoal hover:bg-parchment-dim"
            onClick={() => retry(mailboxId, toast.outboxId)}
          >
            Retry
          </Button>
        </>
      )}
    </motion.div>
  );
}

interface SendToastStackProps {
  mailboxId: string | undefined;
}

/**
 * Fixed-position stack of "Sending in Ns... Undo" toasts — the non-
 * technical-friendly Undo Send affordance from the architecture plan: no
 * keyboard shortcut, just a big always-visible tappable button. Stacks
 * (rather than replacing) so sending two messages back to back — the exact
 * race the outbox/alarm design exists to handle correctly server-side —
 * also *looks* correct client-side: two independent countdowns, two
 * independent Undo buttons.
 */
export function SendToastStack({ mailboxId }: SendToastStackProps) {
  const { toasts } = useOutboxToasts();
  const hasPending = toasts.some((t) => t.status === "pending");
  useTick(hasPending, 250);

  // Stable render order: newest at the bottom, like a normal toast stack.
  const orderedRef = useRef<string[]>([]);
  const seen = new Set(orderedRef.current);
  for (const t of toasts) {
    if (!seen.has(t.outboxId)) orderedRef.current.push(t.outboxId);
  }
  orderedRef.current = orderedRef.current.filter((id) => toasts.some((t) => t.outboxId === id));
  const ordered = orderedRef.current.map((id) => toasts.find((t) => t.outboxId === id)!).filter(Boolean);

  if (!mailboxId) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 bottom-0 z-50 flex flex-col items-center gap-2 px-4 pb-[calc(env(safe-area-inset-bottom)+1rem)] sm:items-end sm:px-6"
    >
      <AnimatePresence>
        {ordered.map((toast) => (
          <ToastRow key={toast.outboxId} toast={toast} mailboxId={mailboxId} />
        ))}
      </AnimatePresence>
    </div>
  );
}
